import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { usePermissions } from '../../context/AuthContext';

const emptyPermissions = (catalog) => {
  const perms = {};
  (catalog?.modules || []).forEach((module) => {
    perms[module.key] = {};
    module.actions.forEach((action) => {
      perms[module.key][action.key] = false;
    });
  });
  return perms;
};

const RolesSettings = () => {
  const { can } = usePermissions();
  const canEdit = can('roles', 'edit');
  const canAdd = can('roles', 'add');
  const canDelete = can('roles', 'delete');

  const [loading, setLoading] = useState(true);
  const [catalog, setCatalog] = useState({ modules: [] });
  const [roles, setRoles] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [form, setForm] = useState({ name: '', code: '', description: '', permissions: {} });
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [catalogRes, rolesRes] = await Promise.all([
        api.get('/access/catalog'),
        api.get('/access/roles')
      ]);
      const cat = catalogRes.data?.data || { modules: [] };
      setCatalog(cat);
      setRoles(rolesRes.data?.data || []);
      if (!editingRole) {
        setForm((prev) => ({
          ...prev,
          permissions: prev.permissions && Object.keys(prev.permissions).length
            ? prev.permissions
            : emptyPermissions(cat)
        }));
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditingRole(null);
    setForm({
      name: '',
      code: '',
      description: '',
      permissions: emptyPermissions(catalog)
    });
    setDialogOpen(true);
  };

  const openEdit = (role) => {
    setEditingRole(role);
    setForm({
      name: role.name,
      code: role.code,
      description: role.description || '',
      permissions: role.permissions || emptyPermissions(catalog)
    });
    setDialogOpen(true);
  };

  const togglePermission = (moduleKey, actionKey) => {
    setForm((prev) => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [moduleKey]: {
          ...prev.permissions[moduleKey],
          [actionKey]: !prev.permissions[moduleKey]?.[actionKey]
        }
      }
    }));
  };

  const handleSave = async () => {
    try {
      if (editingRole) {
        await api.put(`/access/roles/${editingRole.id}`, {
          name: form.name,
          description: form.description,
          permissions: form.permissions
        });
        toast.success('Role updated');
      } else {
        await api.post('/access/roles', form);
        toast.success('Role created');
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save role');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/access/roles/${deleteTarget.id}`);
      toast.success('Role deleted');
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete role');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Roles</Typography>
          <Typography variant="body2" color="text.secondary">
            Define roles with module permissions. Assign roles to groups — users inherit permissions through group membership.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
          {canAdd && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              New Role
            </Button>
          )}
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        System roles (Admin, Manager, Customer) are seeded by default. Permissions are merged when a user belongs to multiple groups.
      </Alert>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Role</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roles.map((role) => (
              <TableRow key={role.id}>
                <TableCell sx={{ fontWeight: 600 }}>{role.name}</TableCell>
                <TableCell><code>{role.code}</code></TableCell>
                <TableCell>{role.description || '—'}</TableCell>
                <TableCell>
                  {role.is_system && <Chip size="small" label="System" color="primary" />}
                  {role.is_locked && <Chip size="small" label="Locked" variant="outlined" sx={{ ml: 0.5 }} />}
                </TableCell>
                <TableCell align="right">
                  {canEdit && (
                    <Tooltip title="Edit permissions">
                      <IconButton size="small" onClick={() => openEdit(role)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canDelete && !role.is_locked && (
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => setDeleteTarget(role)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingRole ? `Edit Role: ${editingRole.name}` : 'Create Role'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Role Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              fullWidth
            />
            {!editingRole && (
              <TextField
                label="Code (optional)"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                helperText="Auto-generated from name if left blank"
                fullWidth
              />
            )}
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              multiline
              rows={2}
              fullWidth
            />

            <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 1 }}>Permissions</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Module</TableCell>
                    {(catalog.modules[0]?.actions || []).map((action) => (
                      <TableCell key={action.key} align="center">{action.label}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {catalog.modules.map((module) => (
                    <TableRow key={module.key}>
                      <TableCell>{module.label}</TableCell>
                      {module.actions.map((action) => (
                        <TableCell key={action.key} align="center">
                          <Checkbox
                            checked={Boolean(form.permissions?.[module.key]?.[action.key])}
                            onChange={() => togglePermission(module.key, action.key)}
                            disabled={!canEdit && editingRole}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.name}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Role</DialogTitle>
        <DialogContent>
          Delete role &quot;{deleteTarget?.name}&quot;? Remove it from all groups first.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RolesSettings;
